/**
 * Colisões do voo (item 4.1): atleta com atleta, com o chão e o teto, com a borda do campo (a mureta da
 * arquibancada) e com os postes e aros dos gols. Os atletas passam pelo furo dos aros; só o tubo bate.
 */
import { FLIGHT, PITCH, hoopCenters } from "@besenliga/shared";
import { FLIGHT_CEILING, flightParams, type AthleteState, type Vec3 } from "./flight.js";

export type CollisionKind = "atleta" | "chao" | "teto" | "borda" | "poste" | "aro";

export interface CollisionEvent {
  kind: CollisionKind;
  /** Atleta envolvido (no choque entre dois atletas, o de id menor). */
  athlete: string;
  /** O outro atleta, no choque entre dois. */
  other?: string;
  /** Velocidade de aproximação no momento do choque (m/s), para faltas e efeitos. */
  impactSpeed: number;
  /** No choque entre dois atletas: quem vinha mais rápido na direção do outro (quem empurrou). */
  pusher?: string;
  /** Velocidade de quem empurrou na direção do outro (m/s): o que conta para a falta. */
  pushSpeed?: number;
}

/** Afasta o atleta da superfície pela normal `n` (unitária) e corta a velocidade que entra nela. */
function pushOut(a: AthleteState, n: Vec3, depth: number): number {
  a.position.x += n.x * depth;
  a.position.y += n.y * depth;
  a.position.z += n.z * depth;
  const vn = a.velocity.x * n.x + a.velocity.y * n.y + a.velocity.z * n.z;
  if (vn >= 0) return 0;
  const k = (1 + FLIGHT.restitution) * vn;
  a.velocity.x -= k * n.x;
  a.velocity.y -= k * n.y;
  a.velocity.z -= k * n.z;
  return -vn;
}

const HOOPS = [...hoopCenters(-1), ...hoopCenters(1)];
const WALL_X = PITCH.sandRadiusX - FLIGHT.athleteRadius;
const WALL_Z = PITCH.sandRadiusZ - FLIGHT.athleteRadius;

function collideWorld(a: AthleteState, events: CollisionEvent[]): void {
  const r = FLIGHT.athleteRadius;
  const add = (kind: CollisionKind, impactSpeed: number) => {
    if (impactSpeed > 0) events.push({ kind, athlete: a.id, impactSpeed });
  };

  // Chão e teto.
  if (a.position.y < FLIGHT.minHeight)
    add("chao", pushOut(a, { x: 0, y: 1, z: 0 }, FLIGHT.minHeight - a.position.y));
  if (a.position.y > FLIGHT_CEILING)
    add("teto", pushOut(a, { x: 0, y: -1, z: 0 }, a.position.y - FLIGHT_CEILING));

  // Borda elíptica do campo: volta para dentro da elipse pela direção do centro e corta a velocidade
  // que sai pela normal (o gradiente da elipse).
  const ux = a.position.x / WALL_X;
  const uz = a.position.z / WALL_Z;
  const e = ux * ux + uz * uz;
  if (e > 1) {
    const s = 1 / Math.sqrt(e);
    const tx = a.position.x * s;
    const tz = a.position.z * s;
    const gx = tx / (WALL_X * WALL_X);
    const gz = tz / (WALL_Z * WALL_Z);
    const gl = Math.sqrt(gx * gx + gz * gz);
    a.position.x = tx;
    a.position.z = tz;
    add("borda", pushOut(a, { x: -gx / gl, y: 0, z: -gz / gl }, 0));
  }

  for (const h of HOOPS) {
    // Poste: cilindro vertical do chão até a parte de baixo do aro.
    const poleTop = h.y - PITCH.hoopRadius;
    if (a.position.y < poleTop + r) {
      const px = a.position.x - h.x;
      const pz = a.position.z - h.z;
      const d = Math.sqrt(px * px + pz * pz);
      const min = FLIGHT.hoopPoleRadius + r;
      if (d < min && d > 1e-6) add("poste", pushOut(a, { x: px / d, y: 0, z: pz / d }, min - d));
    }
    // Aro: toro no plano x = h.x (o aro fica de frente para o campo). Distância até o círculo central do tubo.
    const ox = a.position.x - h.x;
    const oy = a.position.y - h.y;
    const oz = a.position.z - h.z;
    const radial = Math.sqrt(oy * oy + oz * oz);
    if (radial > 1e-6) {
      const cy = (oy / radial) * PITCH.hoopRadius;
      const cz = (oz / radial) * PITCH.hoopRadius;
      const qx = ox;
      const qy = oy - cy;
      const qz = oz - cz;
      const d = Math.sqrt(qx * qx + qy * qy + qz * qz);
      const min = FLIGHT.hoopTubeRadius + r;
      if (d < min && d > 1e-6) add("aro", pushOut(a, { x: qx / d, y: qy / d, z: qz / d }, min - d));
    }
  }
}

/**
 * Choque entre dois atletas: separa pela penetração (cada um anda na proporção inversa da massa) e troca
 * a velocidade da direção do choque com perda (FLIGHT.restitution).
 */
function collidePair(a: AthleteState, b: AthleteState, events: CollisionEvent[]): void {
  const dx = b.position.x - a.position.x;
  const dy = b.position.y - a.position.y;
  const dz = b.position.z - a.position.z;
  const min = 2 * FLIGHT.athleteRadius;
  const d2 = dx * dx + dy * dy + dz * dz;
  if (d2 >= min * min) return;
  const d = Math.sqrt(d2);
  // Exatamente no mesmo ponto: separa pelo eixo x, de forma determinística.
  const n = d > 1e-6 ? { x: dx / d, y: dy / d, z: dz / d } : { x: 1, y: 0, z: 0 };
  const ma = flightParams(a.stats).mass;
  const mb = flightParams(b.stats).mass;
  const wa = mb / (ma + mb);
  const wb = ma / (ma + mb);
  const depth = min - d;
  a.position.x -= n.x * depth * wa;
  a.position.y -= n.y * depth * wa;
  a.position.z -= n.z * depth * wa;
  b.position.x += n.x * depth * wb;
  b.position.y += n.y * depth * wb;
  b.position.z += n.z * depth * wb;
  const rv =
    (b.velocity.x - a.velocity.x) * n.x +
    (b.velocity.y - a.velocity.y) * n.y +
    (b.velocity.z - a.velocity.z) * n.z;
  if (rv >= 0) return;
  // Quem vinha mais rápido na direção do outro, antes do choque.
  const towardB = a.velocity.x * n.x + a.velocity.y * n.y + a.velocity.z * n.z;
  const towardA = -(b.velocity.x * n.x + b.velocity.y * n.y + b.velocity.z * n.z);
  const pusher = towardB >= towardA ? a.id : b.id;
  const pushSpeed = Math.max(towardB, towardA);
  const j = (-(1 + FLIGHT.restitution) * rv) / (1 / ma + 1 / mb);
  a.velocity.x -= (j / ma) * n.x;
  a.velocity.y -= (j / ma) * n.y;
  a.velocity.z -= (j / ma) * n.z;
  b.velocity.x += (j / mb) * n.x;
  b.velocity.y += (j / mb) * n.y;
  b.velocity.z += (j / mb) * n.z;
  events.push({ kind: "atleta", athlete: a.id, other: b.id, impactSpeed: -rv, pusher, pushSpeed });
}

/** Resolve todas as colisões do tick, sempre na mesma ordem (ids em ordem alfabética). Altera `athletes`. */
export function resolveCollisions(athletes: AthleteState[]): CollisionEvent[] {
  const events: CollisionEvent[] = [];
  for (let i = 0; i < athletes.length; i++)
    for (let j = i + 1; j < athletes.length; j++) {
      const a = athletes[i];
      const b = athletes[j];
      if (a && b) collidePair(a, b, events);
    }
  for (const a of athletes) collideWorld(a, events);
  return events;
}
